import { Flex } from '@chakra-ui/layout';
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import {
  CellContext,
  getCoreRowModel,
  useReactTable,
  createColumnHelper,
} from '@tanstack/react-table';
import * as _ from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';

import {
  utils as commonUtils,
  finleyAssertUnreachable,
} from '@finley-technologies/common';
import { Funding } from '@finley-technologies/finley-types';

import { formatNumber } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/commitment-mechanics/[commitmentId]/LenderTable/utils';
import { deletePricingOption } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/pricing-options/dataMappings';
import { usePricingOptionDiffResult } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/pricing-options/hooks';
import { useConfirmDeleteContext } from '~/core/routes/settings/implementations/common/ConfirmDeleteModalContext';
import { useCreditAgreementStore } from '~/core/routes/settings/implementations/common/CreditAgreementProvider/CreditAgreementProvider';
import {
  DiffTag,
  ShowWhileDiffing,
} from '~/core/routes/settings/implementations/common/DiffInputs';
import { IconButtonV2 } from '~/packages/common-frontend/components/IconButton';
import { Icon } from '~/packages/common-frontend/components/icons';
import { TableBETA } from '~/packages/common-frontend/tables/TableBETA';
import { remToPx } from '~/packages/common-frontend/theme/utils';

type TableData = {
  key: string;
  pricingOption: Funding.PricingOption;
};

const EditPricingOptionMenu = ({ row }: CellContext<TableData, any>) => {
  const menuControls = useDisclosure();
  const { showConfirmDeleteModal } = useConfirmDeleteContext();

  const creditAgreement = useCreditAgreementStore(
    (store) => store.creditAgreement,
  );
  const pricingOptionHasAssociatedLoan = creditAgreement?.loanMechanicsV1?.loans.some(
    (loan) => loan.pricingOptionKey === row.original.key,
  );

  const updateCreditAgreement = useCreditAgreementStore(
    (store) => store.updateCreditAgreement,
  );
  const onDeletePricingOption = useCallback(
    (pricingOptionId: string) => {
      updateCreditAgreement((d) => deletePricingOption(d, pricingOptionId));
    },
    [updateCreditAgreement],
  );

  return (
    <Flex
      justify="right"
      onClick={(e) => e.stopPropagation()}
      overflow="hidden"
    >
      <Menu {...menuControls} computePositionOnMount strategy="fixed">
        <MenuButton
          as={IconButtonV2}
          variant="secondary-muted"
          colorScheme="gray"
          isActive={menuControls.isOpen}
          aria-label="Pricing options"
          icon={<Icon icon="ellipsis-vertical" />}
          tooltipEnabled={false}
        />
        <MenuList zIndex="dropdown">
          <MenuItem
            isDisabled={pricingOptionHasAssociatedLoan}
            onClick={() =>
              showConfirmDeleteModal(row.original.pricingOption.name, () => {
                onDeletePricingOption(row.original.key);
              })
            }
          >
            <Tooltip
              isDisabled={!pricingOptionHasAssociatedLoan}
              label="Pricing option cannot be deleted as it is used in a loan"
              placement="top"
            >
              Delete Pricing Option
            </Tooltip>
          </MenuItem>
        </MenuList>
      </Menu>
    </Flex>
  );
};

const RenderPricingOptionName = ({
  row,
  getValue,
}: CellContext<TableData, 'pricingOption.name'>) => {
  const diffResult = usePricingOptionDiffResult(row.original.key);

  return (
    <Flex gap="1rem" alignItems="baseline">
      <ShowWhileDiffing>
        <DiffTag diff={diffResult?.diffResult} disableTooltip />
      </ShowWhileDiffing>
      {getValue()}
    </Flex>
  );
};

const columnHelper = createColumnHelper<TableData>();

const columnDefs = [
  columnHelper.accessor('pricingOption.name', {
    header: 'Pricing Option Name',
    enableSorting: false,
    cell: RenderPricingOptionName,
  }),
  columnHelper.accessor('pricingOption.type', {
    header: 'Type',
    enableSorting: false,
    cell: (info) => commonUtils.toTitleCase(info.getValue()),
  }),
  columnHelper.display({
    id: 'allInRate',
    header: 'All-In-Rate',
    enableSorting: false,
    cell: ({ row }) => {
      const margin = row.original.pricingOption.margin.value;
      let cell = '';

      if (row.original.pricingOption.type === 'benchmark') {
        cell += `${row.original.pricingOption.benchmark} + ${formatNumber(
          margin,
          'percent',
        )}`;
      } else if (row.original.pricingOption.type === 'fixed') {
        cell += `${formatNumber(
          row.original.pricingOption.value + margin,
          'percent',
        )}`;
      } else {
        finleyAssertUnreachable(row.original.pricingOption);
      }

      return cell;
    },
  }),

  columnHelper.display({
    id: 'editPricingOption',
    header: '',
    enableSorting: false,
    minSize: remToPx(1),
    size: remToPx(1),
    cell: EditPricingOptionMenu,
  }),
];

export const PricingOptionsTable = () => {
  const pricingOptions = useCreditAgreementStore((store) => {
    const currentPricingOptions =
      store.creditAgreement?.loanMechanicsV1?.pricingOptions ?? [];
    if (!store.isEditing) {
      const previousPricingOptions =
        store.previousCreditAgreement?.loanMechanicsV1?.pricingOptions ?? [];
      return _.unionBy(
        currentPricingOptions,
        previousPricingOptions,
        (option) => option.key,
      );
    }
    return currentPricingOptions;
  }, _.isEqual);

  const tableData = useMemo(
    () =>
      pricingOptions.map((pricingOption) => ({
        key: pricingOption.key,
        pricingOption,
      })),
    [pricingOptions],
  );

  const table = useReactTable({
    data: tableData,
    columns: columnDefs,
    getCoreRowModel: getCoreRowModel(),
  });

  const navigate = useNavigate();

  return (
    <TableBETA
      table={table}
      onRowClick={(row) => navigate(`${row.original.key}`)}
    />
  );
};
